import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Camera, Loader2 } from "lucide-react";

// Cattura guidata da fotocamera per documenti (es. carta d'identità) — mostra un'anteprima
// a tutto schermo con un riquadro guida (proporzioni tipo carta d'identità/credito, 1.586:1),
// un conto alla rovescia di CAPTURE_COUNTDOWN_SECONDS secondi per dare il tempo di centrare
// il documento, poi scatta SEMPRE quello che si trova nel riquadro in quel momento — niente
// più euristica di rilevamento (edge-energy/stabilità, rimossa: il team UX/UI ha segnalato
// che non si capiva se/quando stesse per scattare, e a volte non scattava affatto se
// l'euristica non si convinceva mai di avere un documento davanti). Il conto alla rovescia
// stesso è il segnale di "pronto": scaduto quello, lo scatto è garantito e immediato, con un
// flash + un breve suono per renderlo evidente.
const CAPTURE_COUNTDOWN_SECONDS = 10;
// Durata del flash visivo sul riquadro al momento dello scatto (manuale o automatico).
const FLASH_MS = 200;

// Breve "click" sintetizzato via Web Audio (nessun file audio da caricare nel progetto) —
// stesso spirito di speech.js::new Audio() per il TTS cloud, ma qui basta un tono secco.
function playShutterSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.value = 1200;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
    osc.onended = () => ctx.close();
  } catch {
    /* audio non disponibile (autoplay bloccato, API assente...) — lo scatto resta comunque
       visibile dal flash, non è bloccante */
  }
}

export default function DocumentCaptureOverlay({ onCapture, onClose, instructions, accentColor = "#0055FF" }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const guideRef = useRef(null);
  const streamRef = useRef(null);
  const capturingRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(CAPTURE_COUNTDOWN_SECONDS);
  const [flash, setFlash] = useState(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const capture = useCallback(
    (sourceRect) => {
      if (capturingRef.current) return;
      capturingRef.current = true;
      playShutterSound();
      setFlash(true);
      setTimeout(() => setFlash(false), FLASH_MS);
      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(sourceRect.sw));
      canvas.height = Math.max(1, Math.round(sourceRect.sh));
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, sourceRect.sx, sourceRect.sy, sourceRect.sw, sourceRect.sh, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          stopStream();
          if (blob) onCapture(blob);
          else {
            capturingRef.current = false;
            setError("Scatto non riuscito, riprova.");
          }
        },
        "image/jpeg",
        0.88
      );
    },
    [onCapture, stopStream]
  );

  // Mappa il riquadro guida (coordinate CSS sullo schermo) alle coordinate pixel native
  // del video — il <video> usa object-fit:cover, quindi la scala è il MAX tra i due
  // rapporti larghezza/altezza (quella che riempie il contenitore ritagliando l'eccesso).
  const sourceRectFromGuide = useCallback(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    const guide = guideRef.current;
    if (!video || !container || !guide || !video.videoWidth) return null;
    const cRect = container.getBoundingClientRect();
    const gRect = guide.getBoundingClientRect();
    const scale = Math.max(cRect.width / video.videoWidth, cRect.height / video.videoHeight);
    const displayedW = video.videoWidth * scale;
    const displayedH = video.videoHeight * scale;
    const offsetX = (displayedW - cRect.width) / 2;
    const offsetY = (displayedH - cRect.height) / 2;
    const sx = (gRect.left - cRect.left + offsetX) / scale;
    const sy = (gRect.top - cRect.top + offsetY) / scale;
    const sw = gRect.width / scale;
    const sh = gRect.height / scale;
    return { sx, sy, sw, sh };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // ideal (non min/exact): non fa mai fallire getUserMedia se il sensore non ci arriva —
        // il browser sceglie semplicemente il valore supportato più vicino, quindi chiedere
        // volutamente più della risoluzione massima reale (es. una camera frontale da tablet,
        // spesso 1080p o anche meno) è innocuo e garantisce di ottenere il massimo disponibile
        // invece di accontentarsi implicitamente dei precedenti 1280x720. Segnalato dal
        // cliente/test sul campo: il riquadro guida, già di per sé una frazione dello
        // schermo, finiva per ritagliare solo poche centinaia di pixel nativi dal frame —
        // troppo pochi per l'OCR del documento lato PMS.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 3840 }, height: { ideal: 2160 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        if (!cancelled) setError("Fotocamera non disponibile: usa l'opzione per caricare un file qui sotto.");
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [stopStream]);

  // Conto alla rovescia — riparte sempre da CAPTURE_COUNTDOWN_SECONDS ad ogni apertura
  // dell'overlay (nuovo mount, nuovo stato).
  useEffect(() => {
    if (!ready || countdown <= 0) return undefined;
    const id = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [ready, countdown]);

  // Scatto automatico GARANTITO allo scadere del countdown — qualunque cosa sia nel
  // riquadro in quel momento, senza più provare a "indovinare" se è un documento. Il rect
  // può non essere ancora misurabile nell'istantaneo in cui countdown tocca 0 (layout non
  // ancora assestato): ritenta ogni 100ms finché non lo è, non si arrende silenziosamente
  // (altrimenti l'overlay resterebbe bloccato senza scattare mai, lo stesso bug segnalato
  // dal cliente sul giro precedente).
  useEffect(() => {
    if (!ready || countdown > 0) return undefined;
    let cancelled = false;
    const tryCapture = () => {
      if (cancelled) return;
      const rect = sourceRectFromGuide();
      if (rect && rect.sw > 0 && rect.sh > 0) {
        capture(rect);
      } else {
        setTimeout(tryCapture, 100);
      }
    };
    tryCapture();
    return () => {
      cancelled = true;
    };
  }, [ready, countdown, sourceRectFromGuide, capture]);

  const manualCapture = () => {
    const rect = sourceRectFromGuide();
    if (rect) capture(rect);
  };

  const onFallbackFile = (file) => {
    if (!file) return;
    stopStream();
    onCapture(file);
  };

  const close = () => {
    stopStream();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black" data-testid="document-capture-overlay">
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
        {flash && <div className="absolute inset-0 bg-white" style={{ opacity: 0.85 }} data-testid="document-capture-flash" />}
        {ready && !error && (
          <div
            ref={guideRef}
            // max-w-md (448px) -> max-w-3xl (768px): su un tablet da banco in verticale
            // (segnalato dal cliente) il container è comunque sotto 768px di larghezza CSS,
            // quindi il cap non scatta più e il riquadro usa per intero il 78% reale dello
            // schermo (più pixel nativi catturati); resta solo come protezione contro un
            // riquadro spropositato su schermi molto larghi in orizzontale (es. test da
            // desktop), dove 78% senza alcun limite sarebbe comunque troppo.
            className="absolute left-1/2 top-1/2 w-[78%] max-w-3xl -translate-x-1/2 -translate-y-1/2 transition-colors"
            style={{ aspectRatio: "1.586", border: `3px solid ${flash ? accentColor : "rgba(255,255,255,0.6)"}`, boxShadow: "0 0 0 2000px rgba(0,0,0,0.45)" }}
            data-testid="document-guide-area"
          >
            {countdown > 0 && (
              <div
                className="absolute inset-0 flex items-center justify-center text-5xl font-bold text-white"
                style={{ textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}
                data-testid="document-capture-countdown"
              >
                {countdown}
              </div>
            )}
          </div>
        )}
        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}
        <button onClick={close} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white" data-testid="document-capture-close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="bg-black px-6 py-5 text-center text-white">
        {error ? (
          <div className="space-y-3">
            <p className="text-sm opacity-80">{error}</p>
            <label className="mx-auto flex w-full max-w-xs cursor-pointer items-center justify-center gap-2 border border-white/30 px-4 py-3 text-sm">
              <Camera className="h-4 w-4" /> Carica documento
              <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => onFallbackFile(e.target.files?.[0])} />
            </label>
          </div>
        ) : (
          <>
            <p className="text-sm opacity-80">
              {ready && countdown > 0
                ? "Preparati: inquadra il documento nel riquadro, lo scatto partirà a breve."
                : instructions || "Inquadra il documento nel riquadro: lo scatto parte da solo."}
            </p>
            <button onClick={manualCapture} disabled={!ready} className="mt-3 text-xs font-semibold uppercase tracking-wide opacity-70 underline disabled:opacity-30" data-testid="document-manual-capture">
              Scatta manualmente
            </button>
          </>
        )}
      </div>
    </div>
  );
}
